import { useEffect, useRef, useState } from 'react'
import { flag } from '../lib/flag'

// Port typeahead: people type "shanghai" or "hamburg", not "CNSHA". Resolves a name (or partial, or
// a raw UN/LOCODE) to its canonical UN/LOCODE via /public/ports/search, while still accepting a
// 5-letter code typed directly. The parent's source of truth is the locode (via onChange); this
// component owns the friendly display text.
const PUBLIC_BASE = (
  import.meta.env.VITE_API_BASE ?? 'https://schedulesmcp-api.fly.dev/v1'
).replace(/\/v1\/?$/, '/public')

interface PortHit {
  unlocode: string
  name: string
  country: string
}
const isLocode = (s: string) => /^[A-Za-z]{5}$/.test(s.trim())

export function PortInput({
  label,
  placeholder,
  seed,
  onChange,
}: {
  label: string
  placeholder: string
  seed: string // locode pushed in from outside (e.g. a "lanes with data" chip)
  onChange: (locode: string) => void
}) {
  const [text, setText] = useState(seed)
  const [hits, setHits] = useState<PortHit[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const lastEmit = useRef(seed)
  // The display text this component last wrote into the box itself (a port NAME after a pick, or the
  // locode when seeded). Distinct from lastEmit, which is always the locode. See the debounce guard.
  const lastDisplay = useRef(seed)
  const boxRef = useRef<HTMLDivElement>(null)

  // External locode (chip click) → reflect it in the box, without clobbering what the user is typing.
  useEffect(() => {
    if (seed && seed !== lastEmit.current) {
      lastEmit.current = seed
      lastDisplay.current = seed
      setText(seed)
      setOpen(false)
    }
  }, [seed])

  // Debounced search.
  //
  // Guarded on BOTH the emitted locode and the display text we wrote ourselves. After pick() the box
  // shows the port NAME while lastEmit holds the LOCODE, so a locode-only guard let the search refire
  // on our own text: the dropdown reopened by itself, and the blur handler below then saw
  // text !== lastEmit and re-committed hits[0], which could silently swap in a DIFFERENT port ~120ms
  // after the user pressed Compare. Neither is something the user did.
  useEffect(() => {
    const q = text.trim()
    if (q.length < 2 || q === lastEmit.current || q === lastDisplay.current) {
      setHits([])
      return
    }
    const id = setTimeout(() => {
      fetch(`${PUBLIC_BASE}/ports/search?q=${encodeURIComponent(q)}`)
        .then((r) => r.json())
        .then((r) => {
          setHits(r?.data?.ports ?? [])
          setActive(0)
        })
        .catch(() => setHits([]))
    }, 170)
    return () => clearTimeout(id)
  }, [text])

  // Dismiss on outside click.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  function emit(locode: string, display: string) {
    lastEmit.current = locode
    lastDisplay.current = display
    setText(display)
    setHits([])
    setOpen(false)
    onChange(locode)
  }
  function pick(h: PortHit) {
    emit(h.unlocode, h.name)
  }
  function commit() {
    if (open && hits[active]) pick(hits[active] as PortHit)
    else if (hits[0]) pick(hits[0] as PortHit)
    else if (isLocode(text)) emit(text.trim().toUpperCase(), text.trim().toUpperCase())
  }

  return (
    <div ref={boxRef} className="relative flex flex-col gap-1">
      <span className="text-xs font-mono uppercase tracking-wide text-[var(--text-muted)]">
        {label}
      </span>
      <input
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          setOpen(true)
        }}
        onFocus={() => hits.length && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
            setActive((a) => Math.min(a + 1, hits.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((a) => Math.max(a - 1, 0))
          } else if (e.key === 'Enter') {
            if (open && hits.length) {
              e.preventDefault()
              commit()
            }
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
        onBlur={() => {
          // Let a click on a suggestion win the race before we auto-resolve free text.
          setTimeout(() => {
            const q = text.trim()
            if (q && q !== lastEmit.current && q !== lastDisplay.current) commit()
          }, 120)
        }}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        className="w-48 rounded-lg border border-border bg-surface-1 px-3 py-2 text-[var(--text-primary)] outline-none focus:border-signal"
      />
      {open && hits.length > 0 && (
        <ul className="absolute top-full left-0 z-20 mt-1 max-h-72 w-72 overflow-auto rounded-lg border border-[var(--border-strong)] bg-[var(--surface-1)] py-1 shadow-xl">
          {hits.map((h, i) => (
            <li key={h.unlocode}>
              <button
                type="button"
                // onMouseDown (not onClick) so it fires before the input's blur handler.
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(h)
                }}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                  i === active ? 'bg-signal/10' : ''
                }`}
              >
                <span className="min-w-0 truncate">
                  {flag(h.country) && <span className="mr-1.5">{flag(h.country)}</span>}
                  <span className="text-[var(--text-primary)]">{h.name}</span>
                  <span className="text-[var(--text-muted)]"> · {h.country}</span>
                </span>
                <span className="shrink-0 font-mono text-[11px] text-[var(--text-muted)]">
                  {h.unlocode}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
